/**
 * Relative time text for the phone layout: "18 h 20 min", "4 min ago",
 * "in 9 h 40 min", and the one clock both the Control Panel card and the
 * dashboard status strip count a pour's time from.
 *
 * Every backend time is naive UTC, so all parsing goes through parseUtc.
 * Every string comes from t('mobile.time.*') with an English default; no
 * em dashes. (Not to be confused with targetStatus.formatElapsed, the
 * desktop "20h 0m" format.)
 */
import { formatInTimeZone, parseUtc } from './datetime';
import type { IsoLike } from './datetime';

/** The slice of i18next's `t` these helpers use; the `t` from
 *  useTranslation() fits it. */
export type TranslateFn = (key: string, options?: any) => string;

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Epoch ms of a backend time, an epoch ms or a Date; null when empty or
 *  invalid. */
const toMs = (value: IsoLike): number | null => {
  const ms = parseUtc(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const daysText = (d: number, t: TranslateFn): string =>
  t('mobile.time.days', { defaultValue: '{{d}} d', d });

/**
 * A length of time, floored to the minute: "under 1 min" | "45 min" |
 * "18 h 20 min" | "3 d 4 h". A whole hour or day drops the zero part
 * ("2 h", "3 d"). Negative spans count as 0; a non-number gives ''.
 */
export const formatDuration = (ms: number, t: TranslateFn): string => {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return '';
  const span = Math.max(0, ms);
  if (span < MINUTE) {
    return t('mobile.time.underOneMin', { defaultValue: 'under 1 min' });
  }
  if (span < HOUR) {
    const m = Math.floor(span / MINUTE);
    return t('mobile.time.minutes', { defaultValue: '{{m}} min', m });
  }
  if (span < DAY) {
    const h = Math.floor(span / HOUR);
    const m = Math.floor((span % HOUR) / MINUTE);
    return m > 0
      ? t('mobile.time.hoursMinutes', { defaultValue: '{{h}} h {{m}} min', h, m })
      : t('mobile.time.hours', { defaultValue: '{{h}} h', h });
  }
  const d = Math.floor(span / DAY);
  const h = Math.floor((span % DAY) / HOUR);
  return h > 0
    ? t('mobile.time.daysHours', { defaultValue: '{{d}} d {{h}} h', d, h })
    : daysText(d, t);
};

/**
 * A length of time in its largest unit only, for tight spots: "36 d" |
 * "18 h" | "45 min" | "under 1 min". Negative spans count as 0; a
 * non-number gives ''.
 */
export const formatSpanShort = (ms: number, t: TranslateFn): string => {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return '';
  const span = Math.max(0, ms);
  if (span >= DAY) return daysText(Math.floor(span / DAY), t);
  if (span >= HOUR) return t('mobile.time.hours', { defaultValue: '{{h}} h', h: Math.floor(span / HOUR) });
  if (span >= MINUTE) return t('mobile.time.minutes', { defaultValue: '{{m}} min', m: Math.floor(span / MINUTE) });
  return t('mobile.time.underOneMin', { defaultValue: 'under 1 min' });
};

/**
 * How long ago `value` was: "just now" | "4 min ago" | "2 h 5 min ago" |
 * "3 d ago" (whole days only once it is a day old). A time ahead of `now`
 * (clock skew between the phone and the server) reads "just now"; an empty
 * or invalid time gives ''.
 */
export const formatAgo = (value: IsoLike | number, now: number, t: TranslateFn): string => {
  const at = toMs(value);
  if (at === null || !Number.isFinite(now)) return '';
  const span = now - at;
  if (span < MINUTE) return t('mobile.time.justNow', { defaultValue: 'just now' });
  const duration = span >= DAY ? daysText(Math.floor(span / DAY), t) : formatDuration(span, t);
  return t('mobile.time.ago', { defaultValue: '{{duration}} ago', duration });
};

/**
 * How long until `value`: "in 9 h 40 min". Under a minute away, or already
 * past, reads "any minute now"; an empty or invalid time gives ''.
 */
export const formatIn = (value: IsoLike | number, now: number, t: TranslateFn): string => {
  const at = toMs(value);
  if (at === null || !Number.isFinite(now)) return '';
  const span = at - now;
  if (span < MINUTE) return t('mobile.time.anyMinute', { defaultValue: 'any minute now' });
  return t('mobile.time.in', { defaultValue: 'in {{duration}}', duration: formatDuration(span, t) });
};

/** The earliest valid time of a list (e.g. every location's
 *  logging_started), as epoch ms; null when none is valid. */
export const earliestTime = (values: ReadonlyArray<IsoLike>): number | null => {
  let earliest: number | null = null;
  values.forEach((value) => {
    const ms = toMs(value);
    if (ms !== null && (earliest === null || ms < earliest)) earliest = ms;
  });
  return earliest;
};

/**
 * A time of the phone's own day, "10:05", with the date ("22 Oct, 10:05")
 * when it is not today: when the data on screen was fetched (the Control
 * Panel and the dashboard say it the same way, in the phone's time zone,
 * not the pour's). '' when invalid.
 */
export const formatLocalClock = (value: IsoLike | number, now: number): string => {
  const ms = typeof value === 'number' ? value : toMs(value);
  if (ms === null || !Number.isFinite(ms)) return '';
  let zone: string | undefined;
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    zone = undefined;
  }
  const sameDay = new Date(ms).toDateString() === new Date(now).toDateString();
  return formatInTimeZone(ms, zone, {
    day: sameDay ? undefined : 'numeric',
    month: sameDay ? undefined : 'short',
    year: undefined,
  });
};

/** Which start a pour's clock counts from:
 *  pour        the pour's recorded start (Started / started_at);
 *  concrete    the concrete start maturity counts from (the earliest EAStart);
 *  monitoring  the first location that started logging;
 *  planned     the planned logger start from pour setup (LoggerStart). */
export type PourClockKind = 'pour' | 'concrete' | 'monitoring' | 'planned';

export interface PourClockStart {
  /** Epoch ms. */
  at: number;
  kind: PourClockKind;
}

export interface PourClockInput {
  /** List row Started; dashboard pour.started_at. */
  started?: IsoLike;
  /** List row ConcreteStart; dashboard pour.concrete_start. */
  concreteStart?: IsoLike;
  /** The earliest location logging_started (dashboard only; see
   *  earliestTime). */
  earliestLoggingStarted?: IsoLike;
  /** List row LoggerStart. */
  loggerStart?: IsoLike;
}

/**
 * The time a pour's "since start" clock counts from, and which start it is,
 * so the Control Panel card and the dashboard strip show the same number
 * and say the same thing. The first valid of: started ('pour'),
 * concreteStart ('concrete'), earliestLoggingStarted ('monitoring'),
 * loggerStart ('planned'). Null when none is set.
 */
export const pourClockStart = ({
  started,
  concreteStart,
  earliestLoggingStarted,
  loggerStart,
}: PourClockInput): PourClockStart | null => {
  const candidates: Array<[IsoLike, PourClockKind]> = [
    [started, 'pour'],
    [concreteStart, 'concrete'],
    [earliestLoggingStarted, 'monitoring'],
    [loggerStart, 'planned'],
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const at = toMs(candidates[i][0]);
    if (at !== null) return { at, kind: candidates[i][1] };
  }
  return null;
};
